import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Search } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { CATEGORIES } from "@/lib/api";

export default function Header() {
  const { count, setDrawerOpen } = useCart();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="luxe-header" data-testid="site-header">
      <div className="hidden md:block bg-[#831132] text-[#FFF4E0] text-[11px] tracking-[0.22em] uppercase text-center py-2 font-medium">
        Complimentary UK delivery on orders over £200 · Crown Yourself
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between py-5">
        <button
          className="md:hidden text-[#1A0F11]"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-open"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {CATEGORIES.slice(0, 4).map((c) => (
            <NavLink
              key={c.slug}
              to={`/shop/${c.slug}`}
              className={({ isActive }) =>
                `text-[12px] tracking-[0.22em] uppercase font-medium transition-colors ${
                  isActive ? "text-[#831132]" : "text-[#1A0F11] hover:text-[#831132]"
                }`
              }
              data-testid={`nav-${c.slug}`}
            >
              {c.label}
            </NavLink>
          ))}
        </nav>

        <Link to="/" className="flex-1 md:flex-none text-center" data-testid="logo-link">
          <div className="font-serif-display text-[#831132] text-2xl md:text-[28px] tracking-tight leading-none">
            <span className="text-[14px] align-top mr-1 tracking-[0.2em]">THE</span>
            HAIR BU&apos;TIK
          </div>
          <div className="overline mt-1 text-[9px] tracking-[0.4em] text-[#831132]/80">Crown Yourself</div>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          {CATEGORIES.slice(4).map((c) => (
            <NavLink
              key={c.slug}
              to={`/shop/${c.slug}`}
              className={({ isActive }) =>
                `text-[12px] tracking-[0.22em] uppercase font-medium transition-colors ${
                  isActive ? "text-[#831132]" : "text-[#1A0F11] hover:text-[#831132]"
                }`
              }
              data-testid={`nav-${c.slug}`}
            >
              {c.label}
            </NavLink>
          ))}
          <Link to="/shop" className="text-[#1A0F11] hover:text-[#831132]" data-testid="search-link" aria-label="Shop all">
            <Search size={18} />
          </Link>
        </div>

        <div className="flex items-center gap-4 md:gap-5">
          <Link
            to={user ? (user.role === "admin" ? "/admin" : "/account") : "/login"}
            className="text-[#1A0F11] hover:text-[#831132]"
            data-testid="account-link"
            aria-label="Account"
          >
            <User size={20} />
          </Link>
          <button
            onClick={() => setDrawerOpen(true)}
            className="relative text-[#1A0F11] hover:text-[#831132]"
            data-testid="cart-button"
            aria-label="Open cart"
          >
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-[#831132] text-[#FFF4E0] text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-medium"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile drawer (rendered via portal to avoid header stacking context bleed) */}
      {mobileOpen && createPortal(
        <div className="fixed inset-0 z-[1000] bg-[#FFF4E0]" data-testid="mobile-menu" style={{ backgroundColor: "#FFF4E0" }}>
          <div className="flex justify-between items-center px-6 py-5 border-b border-[#E8D8C8]">
            <span className="font-serif-display text-[#831132] text-xl">THE HAIR BU&apos;TIK</span>
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
              <X size={22} />
            </button>
          </div>
          <nav className="flex flex-col px-6 py-8 gap-1">
            <Link
              to="/shop"
              onClick={() => setMobileOpen(false)}
              className="font-serif-display text-3xl text-[#1A0F11] py-3 border-b border-[#E8D8C8]"
              data-testid="mobile-nav-shop"
            >
              Shop All
            </Link>
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/shop/${c.slug}`}
                onClick={() => setMobileOpen(false)}
                className="font-serif-display text-3xl text-[#1A0F11] py-3 border-b border-[#E8D8C8]"
                data-testid={`mobile-nav-${c.slug}`}
              >
                {c.label}
              </Link>
            ))}
            <Link to="/about" onClick={() => setMobileOpen(false)} className="font-serif-display text-3xl text-[#1A0F11] py-3 border-b border-[#E8D8C8]" data-testid="mobile-nav-about">About</Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="font-serif-display text-3xl text-[#1A0F11] py-3 border-b border-[#E8D8C8]" data-testid="mobile-nav-contact">Contact</Link>
            <Link to="/faq" onClick={() => setMobileOpen(false)} className="font-serif-display text-3xl text-[#1A0F11] py-3" data-testid="mobile-nav-faq">FAQ</Link>
          </nav>
        </div>,
        document.body
      )}
    </header>
  );
}
